import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { continentsAPI } from '../lib/api';
import { logger } from '../lib/logger';
import { Navbar } from '../components/layout/Navbar';
import { RecipeCard } from '../components/recipes/RecipeCard';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChevronLeft, Utensils } from 'lucide-react';

const COUNTRY_FLAGS = {
  'Maroc': '🇲🇦', 'Tunisie': '🇹🇳', 'Algérie': '🇩🇿', 'Sénégal': '🇸🇳', 'Éthiopie': '🇪🇹',
  'Afrique du Sud': '🇿🇦', 'Égypte': '🇪🇬', 'Nigeria': '🇳🇬', 'Kenya': '🇰🇪', "Côte d'Ivoire": '🇨🇮',
  'Japon': '🇯🇵', 'Chine': '🇨🇳', 'Thaïlande': '🇹🇭', 'Vietnam': '🇻🇳', 'Inde': '🇮🇳',
  'Corée du Sud': '🇰🇷', 'Indonésie': '🇮🇩', 'Philippines': '🇵🇭', 'Malaisie': '🇲🇾', 'Turquie': '🇹🇷',
  'France': '🇫🇷', 'Italie': '🇮🇹', 'Espagne': '🇪🇸', 'Grèce': '🇬🇷', 'Portugal': '🇵🇹',
  'Allemagne': '🇩🇪', 'Royaume-Uni': '🇬🇧', 'Belgique': '🇧🇪', 'Suisse': '🇨🇭', 'Pays-Bas': '🇳🇱',
  'États-Unis': '🇺🇸', 'Mexique': '🇲🇽', 'Canada': '🇨🇦', 'Cuba': '🇨🇺', 'Jamaïque': '🇯🇲',
  'Haïti': '🇭🇹', 'République Dominicaine': '🇩🇴', 'Guatemala': '🇬🇹', 'Honduras': '🇭🇳', 'Costa Rica': '🇨🇷',
  'Brésil': '🇧🇷', 'Argentine': '🇦🇷', 'Pérou': '🇵🇪', 'Colombie': '🇨🇴', 'Chili': '🇨🇱',
  'Venezuela': '🇻🇪', 'Équateur': '🇪🇨', 'Bolivie': '🇧🇴', 'Paraguay': '🇵🇾', 'Uruguay': '🇺🇾',
  'Australie': '🇦🇺', 'Nouvelle-Zélande': '🇳🇿', 'Fidji': '🇫🇯', 'Papouasie-Nouvelle-Guinée': '🇵🇬',
  'Samoa': '🇼🇸', 'Tonga': '🇹🇴', 'Vanuatu': '🇻🇺', 'Nouvelle-Calédonie': '🇳🇨', 'Tahiti': '🇵🇫', 'Hawaï': '🇺🇸',
};

const MEAL_TYPES = [
  { value: 'all', label: 'Toutes' },
  { value: 'entree', label: 'Entrées' },
  { value: 'plat', label: 'Plats' },
  { value: 'dessert', label: 'Desserts' },
];

export default function CountryPage() {
  const { countryName } = useParams();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMealType, setSelectedMealType] = useState('all');

  const decodedCountry = decodeURIComponent(countryName);
  const flag = COUNTRY_FLAGS[decodedCountry] || '🌍';

  useEffect(() => {
    const fetchRecipes = async () => {
      setLoading(true);
      try {
        const params = selectedMealType !== 'all' ? { meal_type: selectedMealType } : {};
        const response = await continentsAPI.getCountryRecipes(decodedCountry, params);
        setRecipes(response.data);
      } catch (error) {
        logger.error('Failed to fetch recipes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecipes();
  }, [decodedCountry, selectedMealType]);

  return (
    <div className="min-h-screen bg-worldplate-cream">
      <Navbar />

      {/* Header */}
      <section className="bg-worldplate-deep py-16 md:py-24" data-testid="country-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/">
            <Button variant="ghost" className="text-white hover:bg-white/20 mb-6 -ml-2" data-testid="back-btn">
              <ChevronLeft className="w-5 h-5 mr-1" />
              Retour
            </Button>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-6"
          >
            <span className="text-7xl md:text-8xl">{flag}</span>
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-2">
                {decodedCountry}
              </h1>
              <div className="flex items-center gap-2 text-white/70">
                <Utensils className="w-5 h-5" />
                <span>{recipes.length} recettes disponibles</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-stone-200 bg-white sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Tabs value={selectedMealType} onValueChange={setSelectedMealType}>
            <TabsList className="bg-worldplate-subtle">
              {MEAL_TYPES.map((type) => (
                <TabsTrigger
                  key={type.value}
                  value={type.value}
                  className="data-[state=active]:bg-worldplate-primary data-[state=active]:text-white"
                  data-testid={`filter-${type.value}`}
                >
                  {type.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </section>

      {/* Recipes */}
      <section className="py-12 md:py-16" data-testid="recipes-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/3] rounded-2xl bg-stone-200 animate-pulse" />
              ))}
            </div>
          ) : recipes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recipes.map((recipe, index) => (
                <RecipeCard key={recipe.id} recipe={recipe} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-worldplate-subtle rounded-full flex items-center justify-center mx-auto mb-6">
                <Utensils className="w-10 h-10 text-worldplate-muted" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-worldplate-deep mb-2">
                Aucune recette trouvée
              </h3>
              <p className="text-worldplate-muted mb-6">
                Soyez le premier à ajouter une recette de {decodedCountry} !
              </p>
              <Link to="/recipes/new">
                <Button className="bg-worldplate-primary hover:bg-worldplate-primary/90 text-white rounded-full px-6" data-testid="add-first-recipe-btn">
                  Ajouter une recette
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
